import React, { useEffect } from "react";
import styled from "styled-components";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TextScramble from "./TextScramble";
import profile from "../images/chenyu-sticker.png";
import linkedInQr from "../images/linkedin-qr.svg";
import xQr from "../images/x-qr.svg";
import weChatQr from "../images/wechat-qr.svg";
// WeChat mark from Simple Icons (CC0).
import { ReactComponent as WeChatIcon } from "../images/wechat-icon.svg";

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const profiles = [
  {
    name: "LinkedIn",
    prefix: "linkedin.com/in/",
    handle: "thechenyuzhang",
    qr: linkedInQr,
    Icon: LinkedInIcon,
  },
  {
    name: "X",
    prefix: "x.com/",
    handle: "chenyuzhang",
    qr: xQr,
    Icon: XIcon,
  },
  {
    name: "WeChat",
    caption: "_chenyu_zhang",
    qr: weChatQr,
    Icon: WeChatIcon,
  },
];

const Page = styled.main`
  min-height: 100vh;
  min-height: 100svh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #000;
  color: #fff;
  font-family: var(--spark-sans, sans-serif);

  @media (max-width: 700px) {
    padding: 1.25rem 1rem;
  }

  @media (max-width: 700px) and (max-height: 700px) {
    padding: 0.5rem 1rem;
  }
`;

const Introduction = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 320px) minmax(0, 580px);
  grid-template-areas:
    "event event"
    "portrait details";
  align-items: center;
  justify-content: center;
  column-gap: clamp(2rem, 6vw, 5rem);
  row-gap: 2rem;
  width: 100%;
  max-width: 1040px;
  text-align: center;

  @media (max-width: 700px) {
    max-width: 460px;
    grid-template-columns: 88px minmax(0, 1fr);
    grid-template-areas:
      "event event"
      "portrait identity"
      "connections connections";
    gap: 1rem;
  }

  @media (max-width: 700px) and (max-height: 700px) {
    gap: 0.75rem;
  }
`;

const Portrait = styled.img`
  grid-area: portrait;
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 4 / 5;
  object-fit: contain;
  margin: 0 auto;

  @media (max-width: 700px) {
    align-self: center;
  }
`;

const TalkHeader = styled.header`
  grid-area: event;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;

  p {
    margin: 0;
    color: #bfbfbf;
    font-size: clamp(0.875rem, 1.8vw, 1.125rem);
    font-weight: 500;
    line-height: 1.4;
  }

  h1 {
    margin: 0;
    color: #fff;
    font-family: var(--spark-sans, sans-serif);
    font-size: clamp(2rem, 4.8vw, 3.5rem);
    font-weight: 600;
    line-height: 1.12;
    letter-spacing: -0.02em;

    > span {
      display: block;
    }
  }

  @media (max-width: 700px) {
    gap: 0.625rem;

    h1 {
      font-size: clamp(1.5rem, 7.2vw, 2rem);
    }
  }
`;

const Details = styled.div`
  grid-area: details;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;

  @media (max-width: 700px) {
    display: contents;
  }
`;

const Identity = styled.div`
  grid-area: identity;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;

  @media (max-width: 700px) {
    gap: 0.625rem;
  }

  @media (max-width: 700px) and (max-height: 700px) {
    gap: 0.5rem;
  }
`;

const Name = styled.p`
  margin: 0;
  font-family: var(--spark-serif, serif);
  font-size: clamp(2.75rem, 4.5vw, 3.5rem);
  font-weight: 400;
  line-height: 1.04;
  letter-spacing: 0;

  span {
    color: var(--spark-accent, #2b6be4);
  }

  @media (max-width: 700px) {
    font-size: clamp(1.75rem, 7.8vw, 2.5rem);
  }
`;

const Mission = styled.p`
  display: flex;
  align-items: center;
  gap: 0.5em;
  margin: 0;
  color: rgba(255, 255, 255, 0.95);
  font-family: var(--spark-sans, sans-serif);
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.45;
  letter-spacing: -0.01em;

  &::before,
  &::after {
    content: "";
    flex: none;
    width: 2.2em;
    height: 0.85em;
    background: currentColor;
    opacity: 0.9;
    -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 126 48'%3E%3Cpath d='M2 40C30 40 60 35 86 22 100 15 112 13 124 16 112 20 104 26 104 26 114 25 121 29 124 36 112 33 103 37 103 37 112 38 118 41 122 46 102 40 78 41 58 40 38 39 18 40 2 42Z'/%3E%3C/svg%3E")
      center / contain no-repeat;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 126 48'%3E%3Cpath d='M2 40C30 40 60 35 86 22 100 15 112 13 124 16 112 20 104 26 104 26 114 25 121 29 124 36 112 33 103 37 103 37 112 38 118 41 122 46 102 40 78 41 58 40 38 39 18 40 2 42Z'/%3E%3C/svg%3E")
      center / contain no-repeat;
  }

  &::before {
    transform: scaleX(-1);
  }

  @media (max-width: 600px) {
    font-size: 1rem;
    line-height: 1.4;

    &::before,
    &::after {
      display: none;
    }
  }
`;

const SocialProfiles = styled.div`
  grid-area: connections;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
  align-self: center;
  gap: 1rem;
  max-width: 560px;
  width: 100%;

  @media (max-width: 700px) {
    gap: 0.5rem;
  }
`;

const QrProfile = styled.figure`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
  margin: 0;
  color: #fff;

  strong {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 6px;
    border: 1px solid #fff;
    border-radius: 999px;
    background: #fff;
    color: #111;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1;

    svg {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }
  }

  figcaption {
    min-height: 2.8em;
    font-size: 0.875rem;
    line-height: 1.4;
    text-align: center;
    overflow-wrap: anywhere;
    color: #bfbfbf;
  }

  @media (max-width: 360px) {
    figcaption {
      font-size: 0.75rem;
    }
  }

  @media (max-width: 700px) and (max-height: 700px) {
    gap: 0.5rem;
  }
`;

const QrImage = styled.div`
  width: 100%;
  max-width: 176px;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 10px;
  background: #fff;

  img {
    display: block;
    width: 100%;
    height: auto;
  }

`;

export default function Sep17Talk() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Timeless Career Lessons for an AI-Native World | Chenyu Zhang";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <Page>
      {/* Match the landing page's hand-drawn text animation. */}
      <svg
        width="0"
        height="0"
        style={{ position: "absolute" }}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          {[3, 10, 17, 24].map((seed, i) => (
            <filter
              id={`jitter-${i}`}
              key={seed}
              x="-5%"
              y="-5%"
              width="110%"
              height="110%"
            >
              <feTurbulence
                type="turbulence"
                baseFrequency="0.04"
                numOctaves="3"
                seed={seed}
                result="noise"
              />
              <feDisplacementMap
                in="SourceGraphic"
                in2="noise"
                scale="3"
                xChannelSelector="R"
                yChannelSelector="G"
              />
            </filter>
          ))}
        </defs>
      </svg>
      <Introduction aria-labelledby="talk-title">
        <TalkHeader>
          <p>Career, Rewired for the AI Age</p>
          <h1 id="talk-title">
            <span><TextScramble loop>Timeless Career Lessons</TextScramble></span>{" "}
            <span><TextScramble loop>for an AI-Native World</TextScramble></span>
          </h1>
        </TalkHeader>
        <Portrait src={profile} alt="Portrait sticker of Chenyu Zhang" width="320" height="400" />
        <Details>
          <Identity>
            <Name className="jitter">
              Chenyu Zhang<span>.</span>
            </Name>
            <Mission className="jitter">
              I am working to ensure that as machines get smarter, humans do too.
            </Mission>
          </Identity>
          <SocialProfiles>
            {profiles.map(({ name, prefix, handle, caption, qr, Icon }) => (
              <QrProfile
                key={name}
                aria-label={`Connect with Chenyu Zhang on ${name}`}
              >
                <strong>
                  <Icon aria-hidden="true" focusable="false" />
                  <span className={name === "X" ? "sr-only" : undefined}>{name}</span>
                </strong>
                <QrImage>
                  <img
                    src={qr}
                    alt={`QR code for Chenyu Zhang's ${name} profile`}
                    width="176"
                    height="176"
                  />
                </QrImage>
                <figcaption>
                  {caption || <>{prefix}<wbr />{handle}</>}
                </figcaption>
              </QrProfile>
            ))}
          </SocialProfiles>
        </Details>
      </Introduction>
    </Page>
  );
}
